import { useCallback, useEffect } from "react";

export type InviteState =
  | "loading"
  | "valid"
  | "invalid"
  | "accepting"
  | "accepted"
  | "error";

type InviteError = {
  code?: string;
  message?: string;
};

export function getInviteError(error: unknown): InviteError {
  return error instanceof Error
    ? { message: error.message, code: (error as InviteError).code }
    : (error as InviteError);
}

type UseInviteAcceptanceOptions<TAcceptResult> = {
  token: string | undefined;
  isLoaded: boolean;
  isSignedIn: boolean | undefined;
  state: InviteState;
  setState: (state: InviteState) => void;
  setError: (error: string | null) => void;
  accept: (token: string) => Promise<TAcceptResult>;
  onAccepted: (result: TAcceptResult) => void | Promise<void>;
  errorMessages: Record<string, string>;
  fallbackMessage: string;
};

export function useInviteAcceptance<TAcceptResult>({
  token,
  isLoaded,
  isSignedIn,
  state,
  setState,
  setError,
  accept,
  onAccepted,
  errorMessages,
  fallbackMessage,
}: UseInviteAcceptanceOptions<TAcceptResult>) {
  const handleAccept = useCallback(async () => {
    if (!token) return;

    setState("accepting");
    setError(null);

    try {
      const result = await accept(token);
      setState("accepted");
      await onAccepted(result);
    } catch (err: unknown) {
      const inviteError = getInviteError(err);
      setState("error");
      setError(
        (inviteError.code && errorMessages[inviteError.code]) ||
          inviteError.message ||
          fallbackMessage,
      );
    }
  }, [
    accept,
    errorMessages,
    fallbackMessage,
    onAccepted,
    setError,
    setState,
    token,
  ]);

  useEffect(() => {
    if (isLoaded && isSignedIn && state === "valid" && token) {
      handleAccept();
    }
  }, [handleAccept, isLoaded, isSignedIn, state, token]);

  return handleAccept;
}
